import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Activity, ClipboardList, DollarSign, FileText, Paperclip, Truck, Upload, X } from "lucide-react";
import Button from "../../../../components/ACS-Dashboard/ACS-Blocks/Button";
import { DisplayDetail } from "../../../../components/ACS-Dashboard/ACS-Blocks/DisplayDetail";
import { ExpandableHeader } from "../../../../components/ACS-Dashboard/ACS-Blocks/ExpandableHeader";
import { claimsApi } from "../api/claimsApi";
import { buildShipmentContextFromDetails, isDraftClaim } from "../claimsMappers";
import {
  CLAIM_LIFECYCLE_STATUSES,
  CLAIM_TYPES,
  ClaimStatusBadge,
  ClaimTypeBadge,
  formatClaimDate,
} from "../claimsShared";
import {
  formatCurrencyInputValue,
  sanitizeCurrencyInput,
  validateClaimForm,
  getTodayLocalDate,
  CLAIM_NUMBER_MAX_LENGTH,
} from "../claimFormUtils";

const ACCEPTED_CLAIM_FILE_TYPES =
  ".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xlsx,.xls,.txt";

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const normalizeSupportingDocuments = (documents = []) =>
  (documents || []).map((doc, index) => ({
    id: doc.id || doc.uuidFilename || `${doc.name}-${index}`,
    documentId: doc.documentId ?? doc.id,
    uuidFilename: doc.uuidFilename,
    name: doc.name,
    size: doc.size,
    file: doc.file || null,
    isExisting: Boolean(doc.isExisting || doc.documentId || doc.uuidFilename || doc.id),
  }));

const EMPTY_FORM = {
  claimNumber: "",
  claimType: "Damage",
  dateIssueOccurred: "",
  dateClaimFiled: getTodayLocalDate(),
  description: "",
  totalClaimAmount: "",
  additionalCosts: "",
  amountApproved: "",
  amountPaid: "",
  status: "Submitted",
  statusEnum: "",
  relatedStopId: "",
  relatedStop: "",
  shipmentNumber: "",
  shipmentOfficeCode: "",
  poNumber: "",
  origin: "",
  destination: "",
};

// Mirrors DisplayDetail's field-box styling exactly, for inputs DisplayDetail doesn't
// natively support (select dropdowns, a $-prefixed currency input) — no shared
// Dropdown/Select or currency-input component exists yet in the platform.
const FIELD_BOX_BASE =
  "relative flex items-center w-full px-fluid-2 py-fluid-1.5 mt-fluid-0.25 rounded-md border text-fluid-sm font-inter focus-within:ring-2 focus-within:ring-blue-400 dark:focus-within:ring-[#223688] focus-within:border-blue-400 dark:focus-within:border-[#223688] transition-none";
const fieldLabelClass = "text-dense-xs text-gray-500 dark:text-acs-dark-subtext";

const FieldSelect = ({ label, value, onChange, children, hasError, hint }) => (
  <div className="flex flex-col min-w-[clamp(8rem,12vw,10rem)]">
    <span className={fieldLabelClass}>{label}</span>
    <div
      className={`${FIELD_BOX_BASE} bg-[#FAFAFB] dark:bg-acs-dark-surface ${
        hasError ? "border-red-500" : "border-gray-300 dark:border-acs-dark-border"
      }`}
    >
      <select
        value={value}
        onChange={onChange}
        className="w-full bg-transparent outline-none focus:outline-none text-acs-light-text dark:text-acs-dark-text"
      >
        {children}
      </select>
    </div>
    {hint && (
      <span className="text-fluid-xs text-gray-400 dark:text-acs-dark-subtext font-medium mt-1">
        {hint}
      </span>
    )}
  </div>
);

const MoneyField = ({ label, value, onChange, onBlur, readOnly, hasError, hint, placeholder = "0.00" }) => (
  <div className="flex flex-col min-w-[clamp(8rem,12vw,10rem)]">
    <span className={fieldLabelClass}>{label}</span>
    <div
      className={`${FIELD_BOX_BASE} ${
        readOnly
          ? "bg-gray-200 dark:bg-acs-dark-surface text-gray-600 dark:text-acs-dark-subtext cursor-not-allowed"
          : "bg-[#FAFAFB] dark:bg-acs-dark-surface"
      } ${hasError ? "border-red-500" : "border-gray-300 dark:border-acs-dark-border"}`}
    >
      <span className="text-gray-400 dark:text-acs-dark-subtext font-semibold mr-1">$</span>
      {readOnly ? (
        <span className="text-acs-light-text dark:text-acs-dark-text truncate">
          {value !== "" ? value : "—"}
        </span>
      ) : (
        <input
          type="text"
          inputMode="decimal"
          value={value}
          placeholder={placeholder}
          onChange={onChange}
          onBlur={onBlur}
          className="w-full bg-transparent outline-none focus:outline-none dark:text-acs-dark-text"
        />
      )}
    </div>
    {hint && (
      <span className="text-fluid-xs text-gray-400 dark:text-acs-dark-subtext font-medium mt-1">
        {hint}
      </span>
    )}
  </div>
);

const ClaimForm = (
  {
    mode = "standalone",
    initialValues = {},
    shipmentContext = null,
    officeCode = "",
    onCancel,
    onSubmit,
    compact = false,
    readOnly = false,
    detailsLocked = false,
    showStatusSection = false,
    isSaving = false,
    // When true, the form doesn't render its own Cancel/Save Draft/Submit/Save
    // Changes row — the caller drives those via the ref instead (e.g. a header
    // bar that hosts the buttons above the form, like ShipmentDetailsView).
    hideFooterActions = false,
    // Errors the server rejected the save with, keyed by form field — e.g. a duplicate
    // Claim Number, which only the backend can detect. Pass a new object per failure.
    fieldErrors = null,
  },
  ref
) => {
  const [form, setForm] = useState({
    ...EMPTY_FORM,
    ...initialValues,
    claimType: initialValues.claimType || "Damage",
    status: initialValues.status || "Submitted",
    statusEnum: initialValues.statusEnum || "",
    relatedStopId: initialValues.relatedStopId ?? "",
    dateClaimFiled:
      initialValues.dateClaimFiled ||
      (initialValues.claimNumber ? "" : getTodayLocalDate()),
  });
  const [errors, setErrors] = useState({});
  const [supportingDocuments, setSupportingDocuments] = useState(() =>
    normalizeSupportingDocuments(initialValues.supportingDocuments)
  );
  const [isDragActive, setIsDragActive] = useState(false);
  const [standaloneContext, setStandaloneContext] = useState(null);
  const [shipmentSearch, setShipmentSearch] = useState(initialValues.shipmentNumber || "");
  const [shipmentResults, setShipmentResults] = useState([]);
  const [isSearchingShipments, setIsSearchingShipments] = useState(false);
  const fileInputRef = useRef(null);
  const searchTimerRef = useRef(null);

  // Server-rejected fields are folded into the same error state as local validation, so editing
  // the field clears the message through the normal setField path.
  useEffect(() => {
    if (fieldErrors && Object.keys(fieldErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...fieldErrors }));
    }
  }, [fieldErrors]);

  const fieldsLocked = readOnly || detailsLocked;
  const isNewClaim = !initialValues.claimNumber;
  // A claim is filed against one shipment and stays there - neither the create nor the update
  // endpoint can move it - so the picker is only live while the claim is being created.
  const shipmentLocked = fieldsLocked || !isNewClaim;
  const isDraftClaimForm =
    isNewClaim || isDraftClaim({ status: form.status, statusEnum: form.statusEnum });
  const isDraftOrNew = isDraftClaimForm;

  const activeContext = useMemo(() => {
    if (mode === "shipment" && shipmentContext) {
      return shipmentContext;
    }
    if (mode === "standalone") {
      return standaloneContext;
    }
    return null;
  }, [mode, shipmentContext, standaloneContext]);

  const availableStops = activeContext?.stops || [];

  useEffect(() => {
    if (mode !== "standalone" || !form.shipmentNumber) return;

    const lookupOfficeCode = form.shipmentOfficeCode || officeCode;
    if (!lookupOfficeCode) return;

    let cancelled = false;
    claimsApi
      .getShipmentDetails(form.shipmentNumber, lookupOfficeCode)
      .then((details) => {
        if (cancelled) return;
        const ctx = buildShipmentContextFromDetails(details);
        setStandaloneContext(ctx);
        setForm((prev) => ({
          ...prev,
          poNumber: ctx.poNumber,
          origin: ctx.origin,
          destination: ctx.destination,
          shipmentOfficeCode: ctx.officeCode || prev.shipmentOfficeCode,
          relatedStopId:
            prev.relatedStopId ||
            ctx.stops?.[ctx.stops.length - 1]?.id ||
            "",
          relatedStop:
            prev.relatedStop ||
            ctx.stops?.[ctx.stops.length - 1]?.label ||
            "",
        }));
      })
      .catch(() => {
        if (!cancelled) setStandaloneContext(null);
      });

    return () => {
      cancelled = true;
    };
  }, [form.shipmentNumber, form.shipmentOfficeCode, mode, officeCode]);

  useEffect(() => {
    if (mode !== "standalone" || shipmentLocked) return;
    // Don't auto-search when the field still matches the already-selected shipment
    // (e.g. right after opening an existing claim) — only once the user actually
    // types something different.
    if (!officeCode || shipmentSearch.trim().length < 2 || shipmentSearch === form.shipmentNumber) {
      setShipmentResults([]);
      return;
    }

    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      setIsSearchingShipments(true);
      try {
        const response = await claimsApi.searchShipments(shipmentSearch, officeCode, { size: 10 });
        setShipmentResults(response?.content || []);
      } catch {
        setShipmentResults([]);
      } finally {
        setIsSearchingShipments(false);
      }
    }, 300);

    return () => clearTimeout(searchTimerRef.current);
  }, [shipmentSearch, mode, officeCode, shipmentLocked, form.shipmentNumber]);

  const selectShipment = (shipment) => {
    const shipmentNumber = shipment?.shipmentNumber || shipment;
    const shipmentOfficeCode = shipment?.officeCode || "";
    setShipmentSearch(shipmentNumber);
    setShipmentResults([]);
    setForm((prev) => ({
      ...prev,
      shipmentNumber,
      shipmentOfficeCode,
    }));
    if (errors.shipmentNumber) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.shipmentNumber;
        return next;
      });
    }
  };

  const addFiles = (fileList) => {
    if (readOnly) return;
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;

    setSupportingDocuments((prev) => [
      ...prev,
      ...incoming.map((file) => ({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
        name: file.name,
        size: file.size,
        file,
      })),
    ]);
  };

  const handleFileInputChange = (e) => {
    addFiles(e.target.files);
    e.target.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!readOnly) setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    addFiles(e.dataTransfer.files);
  };

  const removeDocument = (id) => {
    setSupportingDocuments((prev) => prev.filter((doc) => doc.id !== id));
  };

  const openFilePicker = () => {
    if (readOnly) return;
    fileInputRef.current?.click();
  };

  const buildFormPayload = () => ({
    ...form,
    supportingDocuments,
    totalClaimAmount: formatCurrencyInputValue(form.totalClaimAmount) || form.totalClaimAmount,
    additionalCosts: formatCurrencyInputValue(form.additionalCosts) || form.additionalCosts,
  });

  const setField = (key, value) => {
    if (readOnly) return;
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleCurrencyChange = (key, raw) => {
    setField(key, sanitizeCurrencyInput(raw));
  };

  const handleCurrencyBlur = (key) => {
    if (readOnly) return;
    const formatted = formatCurrencyInputValue(form[key]);
    if (formatted !== "") {
      setField(key, formatted);
    }
  };

  /**
   * Runs validateClaimForm and publishes any errors. Every save path goes through this, because
   * Claim Number is required to save at all (LGX-1047) — not only to submit.
   */
  const runValidation = (payload, { submit = false } = {}) => {
    const { valid, errors: validationErrors } = validateClaimForm(payload, {
      submit,
      isNewClaim,
    });
    setErrors(validationErrors);
    return valid;
  };

  const handleSaveDraft = () => {
    const payload = { ...buildFormPayload(), mode };
    if (!runValidation(payload)) return;
    onSubmit?.(payload, { submit: false, draft: true });
  };

  const handleSaveChanges = () => {
    const payload = { ...buildFormPayload(), mode };
    if (!runValidation(payload)) return;
    onSubmit?.(payload, { submit: false, draft: false });
  };

  const handleSubmit = () => {
    const payload = { ...buildFormPayload(), mode };
    if (!runValidation(payload, { submit: true })) return;
    onSubmit?.(payload, { submit: true });
  };

  useImperativeHandle(ref, () => ({
    saveDraft: handleSaveDraft,
    submit: handleSubmit,
    saveChanges: handleSaveChanges,
  }));

  // Matches the blue ExpandableHeader section pattern used across Shipments/Invoices
  // (e.g. "Core Shipment Details", "Bill To"/"Pay To"). Nested one shade lighter
  // (secondary) when compact/embedded inside ShipmentClaims' own primary header.
  const sectionHeaderType = compact ? "secondary" : "primary";
  const sectionBodyClass = "p-fluid-2";
  const subClass = compact
    ? "text-fluid-xs text-gray-500 dark:text-acs-dark-subtext mt-0 mb-fluid-2"
    : "text-fluid-sm text-gray-500 dark:text-acs-dark-subtext mt-0 mb-fluid-2";
  const errorClass = "text-fluid-xs text-red-600 dark:text-red-400 font-semibold mt-0.5";

  const renderPillRow = () => {
    if (fieldsLocked) {
      return <ClaimTypeBadge type={form.claimType} />;
    }
    return (
      <div className="flex flex-wrap gap-2">
        {CLAIM_TYPES.map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => setField("claimType", type)}
            className={`border rounded-md px-3.5 py-2 text-fluid-xs font-semibold transition-none ${
              form.claimType === type
                ? "border-acs-primary-navy bg-custom-bg-light-purple text-acs-primary-navy dark:border-[#6DE1C2] dark:bg-acs-dark-hover dark:text-[#6DE1C2]"
                : "border-gray-300 dark:border-acs-dark-border bg-[#FAFAFB] dark:bg-acs-dark-surface text-gray-600 dark:text-acs-dark-subtext"
            }`}
          >
            {type}
          </button>
        ))}
      </div>
    );
  };

  const renderMoneyInput = (key, { forceReadOnly = false, hint, lockWhenDetailsLocked = true } = {}) => {
    const isFieldReadOnly =
      readOnly ||
      forceReadOnly ||
      (lockWhenDetailsLocked && detailsLocked);
    const labels = {
      totalClaimAmount: "Total Claim Amount",
      additionalCosts: "Additional Costs",
      amountApproved: "Claim Amount Approved",
      amountPaid: "Claim Amount Paid",
    };
    const displayValue =
      isFieldReadOnly && form[key] !== ""
        ? formatCurrencyInputValue(form[key]) || form[key]
        : form[key];

    return (
      <div className="flex flex-col gap-1.5">
        <MoneyField
          label={labels[key]}
          value={displayValue}
          readOnly={isFieldReadOnly}
          hasError={Boolean(errors[key])}
          hint={hint}
          onChange={(e) => handleCurrencyChange(key, e.target.value)}
          onBlur={() => handleCurrencyBlur(key)}
        />
        {errors[key] && <span className={errorClass}>{errors[key]}</span>}
      </div>
    );
  };

  const claimDetailsSubtitle =
    mode === "shipment" && shipmentContext
      ? `Filed against this shipment — carrier ${shipmentContext.carrierName || "—"}, PO ${shipmentContext.poNumber || "—"}`
      : "What happened, and when.";

  return (
    <div className="font-roboto">
      {mode === "standalone" && (
        <ExpandableHeader headerSection="Related Shipment" icon={Truck} headerType={sectionHeaderType}>
        <div className={sectionBodyClass}>
          <p className={subClass}>Link this claim to the shipment it concerns.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-fluid-2">
            <div className="flex flex-col gap-1.5 md:col-span-2">
              {shipmentLocked ? (
                <DisplayDetail label="Shipment Number" value={form.shipmentNumber} />
              ) : (
                <>
                  <span className={fieldLabelClass}>Shipment Number</span>
                  <div className="relative">
                    <div
                      className={`${FIELD_BOX_BASE} mt-fluid-0.25 bg-[#FAFAFB] dark:bg-acs-dark-surface ${
                        errors.shipmentNumber ? "border-red-500" : "border-gray-300 dark:border-acs-dark-border"
                      }`}
                    >
                      <input
                        type="text"
                        value={shipmentSearch}
                        onChange={(e) => {
                          setShipmentSearch(e.target.value);
                          if (!e.target.value.trim()) {
                            setField("shipmentNumber", "");
                            setStandaloneContext(null);
                          }
                        }}
                        placeholder="Search by shipment number..."
                        className="w-full bg-transparent outline-none focus:outline-none dark:text-acs-dark-text"
                      />
                      {isSearchingShipments && (
                        <span className="flex-shrink-0 text-fluid-xs text-gray-400">Searching...</span>
                      )}
                    </div>
                    {shipmentResults.length > 0 && (
                      <ul className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto border border-gray-200 dark:border-acs-dark-border rounded-md bg-white dark:bg-acs-dark-surface shadow-lg">
                        {shipmentResults.map((shipment) => (
                          <li key={shipment.shipmentNumber}>
                            <button
                              type="button"
                              onClick={() => selectShipment(shipment)}
                              className="w-full text-left px-3 py-2 text-fluid-sm hover:bg-gray-50 dark:hover:bg-acs-dark-hover"
                            >
                              {shipment.shipmentNumber}
                              {shipment.poNumber ? ` · PO ${shipment.poNumber}` : ""}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {errors.shipmentNumber && (
                    <span className={errorClass}>{errors.shipmentNumber}</span>
                  )}
                </>
              )}
            </div>
            <DisplayDetail label="PO Number" value={form.poNumber || activeContext?.poNumber || ""} />
            <DisplayDetail label="Origin" value={form.origin || activeContext?.origin || ""} />
            <DisplayDetail label="Destination" value={form.destination || activeContext?.destination || ""} />
          </div>
        </div>
        </ExpandableHeader>
      )}

      <ExpandableHeader headerSection="Claim Details" icon={ClipboardList} headerType={sectionHeaderType}>
      <div className={sectionBodyClass}>
        <p className={subClass}>{claimDetailsSubtitle}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-fluid-2">
          {/*
            Claim Number is entered by the person filing the claim (LGX-1047). It stays editable
            only while the claim is being created — afterwards it's the claim's identity, used in
            every API path and on its documents, so it's shown read-only.
          */}
          <div className="flex flex-col gap-1.5">
            {isNewClaim && !fieldsLocked ? (
              <>
                <label className={fieldLabelClass} htmlFor="claim-number">
                  Claim Number
                </label>
                <div
                  className={`${FIELD_BOX_BASE} bg-[#FAFAFB] dark:bg-acs-dark-surface ${
                    errors.claimNumber
                      ? "border-red-500"
                      : "border-gray-300 dark:border-acs-dark-border"
                  }`}
                >
                  <input
                    id="claim-number"
                    type="text"
                    value={form.claimNumber || ""}
                    onChange={(e) => setField("claimNumber", e.target.value)}
                    onBlur={(e) => setField("claimNumber", e.target.value.trim())}
                    maxLength={CLAIM_NUMBER_MAX_LENGTH}
                    placeholder="Enter the claim number"
                    aria-invalid={Boolean(errors.claimNumber)}
                    className="w-full bg-transparent outline-none focus:outline-none dark:text-acs-dark-text"
                  />
                </div>
                {errors.claimNumber && (
                  <span className={errorClass}>{errors.claimNumber}</span>
                )}
              </>
            ) : (
              <DisplayDetail label="Claim Number" value={form.claimNumber || ""} />
            )}
          </div>

          <div className="md:col-span-2 flex flex-col gap-1.5">
            <label className={fieldLabelClass}>Claim Type</label>
            {renderPillRow()}
            {errors.claimType && <span className={errorClass}>{errors.claimType}</span>}
          </div>

          {(availableStops.length > 0 || form.relatedStopId) && (
            <div className="md:col-span-2 flex flex-col gap-1.5">
              {fieldsLocked || availableStops.length === 0 ? (
                <DisplayDetail
                  label="Related Stop"
                  value={form.relatedStop || (form.relatedStopId ? `Stop #${form.relatedStopId}` : "")}
                />
              ) : (
                <FieldSelect
                  label="Related Stop"
                  value={String(form.relatedStopId ?? "")}
                  hasError={Boolean(errors.relatedStopId)}
                  hint="Which stop the damage, shortage, or delay was identified at."
                  onChange={(e) => {
                    const nextId = e.target.value ? Number(e.target.value) : "";
                    const stop = availableStops.find((s) => String(s.id) === String(nextId));
                    setForm((prev) => ({
                      ...prev,
                      relatedStopId: nextId,
                      relatedStop: stop?.label || "",
                    }));
                    if (errors.relatedStopId) {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.relatedStopId;
                        return next;
                      });
                    }
                  }}
                >
                  {!form.relatedStopId && (
                    <option value="">Select a stop</option>
                  )}
                  {form.relatedStopId &&
                    !availableStops.some((stop) => String(stop.id) === String(form.relatedStopId)) && (
                      <option value={form.relatedStopId}>
                        {form.relatedStop || `Stop #${form.relatedStopId}`}
                      </option>
                    )}
                  {availableStops.map((stop) => (
                    <option key={stop.id} value={stop.id}>
                      {stop.label}
                    </option>
                  ))}
                </FieldSelect>
              )}
              {errors.relatedStopId && (
                <span className={errorClass}>{errors.relatedStopId}</span>
              )}
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <DisplayDetail
              label="Date Issue Occurred"
              value={fieldsLocked ? formatClaimDate(form.dateIssueOccurred) : form.dateIssueOccurred}
              onChange={(value) => setField("dateIssueOccurred", value)}
              isEditable
              isReadOnly={fieldsLocked}
              inputType="date"
              showCalendar
            />
            {errors.dateIssueOccurred && (
              <span className={errorClass}>{errors.dateIssueOccurred}</span>
            )}
          </div>

          {/*
            Always read-only: the date filed is stamped by the backend when the claim is
            submitted and is not part of the create or update payload, so an editable field
            here would silently discard whatever the user typed.
          */}
          <div className="flex flex-col gap-1.5">
            <DisplayDetail
              label="Date Claim Filed"
              value={formatClaimDate(form.dateClaimFiled)}
              isEditable={false}
              isReadOnly
              inputType="date"
            />
            {errors.dateClaimFiled && (
              <span className={errorClass}>{errors.dateClaimFiled}</span>
            )}
          </div>

          <div className="md:col-span-2 flex flex-col gap-1.5">
            <DisplayDetail
              label="Claim Description"
              value={form.description}
              onChange={(value) => setField("description", value)}
              isEditable
              isReadOnly={fieldsLocked}
              multiline
              placeholder={
                readOnly
                  ? undefined
                  : "Brief summary of what happened (e.g. 4 pallets crushed in transit, received 06/22)."
              }
            />
            {errors.description && <span className={errorClass}>{errors.description}</span>}
          </div>
        </div>
      </div>
      </ExpandableHeader>

      <ExpandableHeader headerSection="Claim Amounts" icon={DollarSign} headerType={sectionHeaderType}>
      <div className={sectionBodyClass}>
        <p className={subClass}>Dollar figures only — no product valuation language is captured here.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-fluid-2">
          {renderMoneyInput("totalClaimAmount", {
            hint: "The full amount being claimed against the carrier.",
          })}
          {renderMoneyInput("additionalCosts", {
            hint: "Rework, disposal, repack, inspection, etc.",
          })}
          {renderMoneyInput("amountApproved", {
            forceReadOnly: isNewClaim,
            lockWhenDetailsLocked: false,
            hint: isNewClaim
              ? "Set once the carrier responds — not entered at filing."
              : "Carrier-approved amount.",
          })}
          {renderMoneyInput("amountPaid", {
            forceReadOnly: isNewClaim,
            lockWhenDetailsLocked: false,
            hint: isNewClaim
              ? "Updates automatically as payments are logged."
              : "Total paid to date.",
          })}
        </div>
      </div>
      </ExpandableHeader>

      {showStatusSection && !isDraftClaimForm && (
        <ExpandableHeader headerSection="Claim Status" icon={Activity} headerType={sectionHeaderType}>
        <div className={sectionBodyClass}>
          <p className={subClass}>Tracks the claim from filing through resolution.</p>
          <div className="flex flex-col gap-1.5">
            {readOnly ? (
              <>
                <label className={fieldLabelClass}>Status</label>
                <div className="pt-0.5">
                  <ClaimStatusBadge status={form.status} uppercase={false} />
                </div>
              </>
            ) : (
              <FieldSelect
                label="Status"
                value={form.status}
                onChange={(e) => setField("status", e.target.value)}
              >
                {CLAIM_LIFECYCLE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </FieldSelect>
            )}
          </div>
        </div>
        </ExpandableHeader>
      )}

      {showStatusSection && isDraftClaimForm && !isNewClaim && (
        <ExpandableHeader headerSection="Claim Status" icon={Activity} headerType={sectionHeaderType}>
        <div className={sectionBodyClass}>
          <p className={subClass}>This claim is saved as a draft and can be submitted when ready.</p>
          <div className="flex flex-col gap-1.5">
            <label className={fieldLabelClass}>Status</label>
            <div className="pt-0.5">
              <ClaimStatusBadge status="Draft" uppercase={false} />
            </div>
          </div>
        </div>
        </ExpandableHeader>
      )}

      {!readOnly && (
        <ExpandableHeader headerSection="Supporting Documents" icon={Paperclip} headerType={sectionHeaderType}>
        <div className={sectionBodyClass}>
          <p className={subClass}>
            Photos, BOLs, inspection reports, correspondence with the carrier. Optional — not required to submit.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_CLAIM_FILE_TYPES}
            onChange={handleFileInputChange}
            className="hidden"
          />
          <div
            role="button"
            tabIndex={0}
            onClick={openFilePicker}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openFilePicker();
              }
            }}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border border-dashed rounded-md p-fluid-2 text-center text-gray-400 dark:text-acs-dark-subtext text-fluid-xs font-semibold bg-[#FAFAFB] dark:bg-acs-dark-surface cursor-pointer transition-none ${
              isDragActive
                ? "border-acs-primary-navy bg-custom-bg-light-purple dark:bg-acs-dark-hover"
                : "border-gray-300 hover:border-[#223688]"
            }`}
          >
            <Upload className="w-5 h-5 mx-auto mb-2 text-gray-400" />
            <div>
              <b className="text-acs-light-text dark:text-acs-dark-text">Click to upload</b> or drag
              files here
            </div>
          </div>
          {supportingDocuments.length > 0 && (
            <ul className="mt-fluid-2 flex flex-col gap-2">
              {supportingDocuments.map((doc) => (
                <li
                  key={doc.id}
                  className="flex items-center justify-between gap-3 border border-gray-200 dark:border-acs-dark-border rounded-md px-3 py-2 bg-white dark:bg-acs-dark-surface"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-fluid-sm font-semibold text-acs-light-text dark:text-acs-dark-text truncate">
                      {doc.name}
                    </span>
                    {doc.size != null && (
                      <span className="text-fluid-xs text-gray-400 flex-shrink-0">
                        {formatFileSize(doc.size)}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeDocument(doc.id)}
                    className="text-gray-400 hover:text-red-600 flex-shrink-0"
                    aria-label={`Remove ${doc.name}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        </ExpandableHeader>
      )}

      {readOnly && (
        <ExpandableHeader headerSection="Supporting Documents" icon={Paperclip} headerType={sectionHeaderType}>
        <div className={sectionBodyClass}>
          <p className={subClass}>Photos, BOLs, inspection reports, correspondence with the carrier.</p>
          {supportingDocuments.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {supportingDocuments.map((doc) => (
                <li
                  key={doc.id}
                  className="flex items-center gap-2 border border-gray-200 dark:border-acs-dark-border rounded-md px-3 py-2 bg-[#FAFAFB] dark:bg-acs-dark-surface"
                >
                  <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-fluid-sm font-semibold text-acs-light-text dark:text-acs-dark-text truncate">
                    {doc.name}
                  </span>
                  {doc.size != null && (
                    <span className="text-fluid-xs text-gray-400">{formatFileSize(doc.size)}</span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-fluid-xs text-gray-400 font-medium">No documents attached.</p>
          )}
        </div>
        </ExpandableHeader>
      )}

      {!hideFooterActions && (
        <div className={`flex justify-end gap-2 ${compact ? "pt-1.5" : "pb-fluid-2 pt-1"}`}>
          <Button
            text={readOnly ? "Close" : "Cancel"}
            variant="outline"
            onClick={onCancel}
            disabled={isSaving}
          />
          {!readOnly && (
            <>
              {isDraftOrNew && (
                <Button
                  text={isSaving ? "Saving..." : "Save Draft"}
                  variant="outline"
                  onClick={handleSaveDraft}
                  disabled={isSaving}
                />
              )}
              <Button
                text={
                  isSaving
                    ? "Saving..."
                    : isDraftOrNew
                      ? "Submit Claim"
                      : "Save Changes"
                }
                onClick={isDraftOrNew ? handleSubmit : handleSaveChanges}
                disabled={isSaving}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default forwardRef(ClaimForm);
